import { removeWorkspace, removeRole, removeOrg, clearBrowsingMemberWorkspace } from "../api/workspace";
import { createClient } from "@supabase/supabase-js";
import { clearSupergrow2Storage } from "../context/supergrow2-context";
import { getSignupAttribution } from "./signup-attribution";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_API_KEY
);

/**
 * Validates that the given URL is a safe, relative in-app path.
 * Rejects absolute URLs, protocol-relative URLs, and other unsafe patterns.
 */
export function isAllowedRedirectPath(url) {
  if (typeof url !== "string") return false;
  // Must start with "/" and must not start with "//" (protocol-relative)
  if (!url.startsWith("/") || url.startsWith("//")) return false;
  try {
    // If it parses as an absolute URL, reject it
    new URL(url);
    return false;
  } catch {
    // Not an absolute URL – that's what we want
    return true;
  }
}

export function sanitizeRedirectUrl(url) {
  if (
    typeof url === "string" &&
    (url.indexOf("login") !== -1 || url.indexOf("signup") !== -1)
  ) {
    return "/";
  }
  return isAllowedRedirectPath(url) ? url : "/";
}


// This is only for login and signup with LinkedIn
export function getLinkedInScopes() {
  return "openid email profile w_member_social";
}

export function getLinkedInScopesV2() {
  return "r_organization_social rw_organization_admin w_member_social w_organization_social r_basicprofile r_1st_connections_size email r_emailaddress";
}

export function getLinkedInScopesV3() {
  return "r_basicprofile r_1st_connections_size r_member_postAnalytics r_member_profileAnalytics w_member_social w_member_social_feed r_organization_followers r_organization_social r_organization_social_feed rw_organization_admin w_organization_social w_organization_social_feed";
}

const toTokenResponse = (session) => ({
  provider_token: session.provider_token,
  access_token: session.access_token,
  refresh_token: session.refresh_token,
});

// No local cache: getSession refreshes a token near expiry, and a cached
// token outlived its setTimeout in background tabs and after sleep.
export async function retrieveToken() {
  const { data } = await supabase.auth.getSession();
  if (!data?.session) throw new Error("No session");
  return toTokenResponse(data.session);
}

let refreshing = null;

// The API rejected our token, so force a refresh even if it looks unexpired.
// Parallel 401s share one refresh; auth-js runs each refreshSession call separately.
export function refreshAccessToken() {
  refreshing ??= supabase.auth
    .refreshSession()
    .then(({ data, error }) => {
      if (error || !data?.session) throw error || new Error("No session");
      return toTokenResponse(data.session);
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

// Supabase Auth is the OAuth 2.1 authorization server for MCP clients such as
// Claude. It sends the user to /oauth/consent with an authorization_id; these
// wrap the consent calls so the Supabase client stays private to this module.
export function getOAuthAuthorizationDetails(authorizationId) {
  return supabase.auth.oauth.getAuthorizationDetails(authorizationId);
}

export function approveOAuthAuthorization(authorizationId) {
  return supabase.auth.oauth.approveAuthorization(authorizationId);
}

export function denyOAuthAuthorization(authorizationId) {
  return supabase.auth.oauth.denyAuthorization(authorizationId);
}

export async function logout() {
  removeWorkspace();
  removeRole();
  removeOrg();
  clearBrowsingMemberWorkspace();
  clearSupergrow2Storage();
  await supabase.auth.signOut({ scope: 'local' });
}

export async function loginWithLinkedIn(redirectUrl, userType = null) {
  const safeRedirect = sanitizeRedirectUrl(redirectUrl);
  let callbackUrl =
    window.location.origin +
    "/auth/linkedin/callback?redirectUrl=" +
    encodeURIComponent(safeRedirect);
  if (userType) {
    callbackUrl += "&user_type=" + encodeURIComponent(userType);
  }
  await supabase.auth.signInWithOAuth({
    provider: "linkedin_oidc",
    options: {
      scopes: getLinkedInScopes(),
      redirectTo: callbackUrl,
    },
  });
}

export async function loginWithGoogle(redirectUrl, userType = null) {
  const safeRedirect = sanitizeRedirectUrl(redirectUrl);
  let callbackUrl =
    window.location.origin +
    "/auth/google/callback?redirectUrl=" +
    encodeURIComponent(safeRedirect);
  if (userType) {
    callbackUrl += "&user_type=" + encodeURIComponent(userType);
  }
  await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: callbackUrl,
    },
  });
}

export async function signupWithEmailPassword(email, password, name, userType = null) {
  let emailRedirectUrl = window.location.origin + "/auth/email/callback";
  if (userType) {
    emailRedirectUrl += "?user_type=" + encodeURIComponent(userType);
  }
  return supabase.auth.signUp({
    email: email,
    password: password,
    options: {
      emailRedirectTo: emailRedirectUrl,
      data: {
        first_name: name,
        // The confirmation link can open in another browser, where localStorage is empty.
        signup_attribution: getSignupAttribution(),
      },
    },
  });
}

export async function loginWithEmailPassword(email, password) {
  return supabase.auth.signInWithPassword({
    email: email,
    password: password,
    options: {
      emailRedirectTo: window.location.origin + "/auth/email/callback",
    },
  });
}

export async function updatePassword(password) {
  return supabase.auth.updateUser({
    password: password,
  });
}

export async function resetPasswordForEmail(email) {
  const result = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + "/auth/email/update-password",
  });
  // Supabase returns errors instead of throwing; callers would show "email sent" on a failed send.
  if (result.error) throw result.error;
  return result;
}

export async function getUserData() {
  return new Promise((resolve, reject) => {
    supabase.auth.getUser().then((value) => {
      if (value.data?.user) {
        resolve(value.data.user);
      }
      reject();
    });
  });
}

// getSession reads local storage, so login does not wait on a network call.
export async function getSessionSignupAttribution() {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.user_metadata?.signup_attribution ?? null;
}

export async function markSignupTracked(attribution) {
  return supabase.auth.updateUser({
    data: { signup_attribution: attribution, signup_tracked_at: new Date().toISOString() },
  });
}

export function getOAuthProviderFromUrl(url) {
  if (url.includes("/auth/google/")) {
    return "Google";
  } else if (url.includes("/auth/linkedin/")) {
    return "LinkedIn";
  } else {
    return "Email";
  }
}